<template>
  <div :class="`examples-in-use theme-${color.color1} theme2-${color.color2}`">
    <div class="row align-bottom">
      <div>
        <h3>Icons in Action</h3>
        <p class="line-length-xl">
          Ever wonder what &ldquo;{{ icon.name }}&rdquo; might look like in the mix? Well, you're in luck! We've cooked up some examples of how you could use
          the &ldquo;{{ icon.name }}&rdquo; icon in your projects, whether they're apps, interfaces, or print designs.
        </p>
      </div>
    </div>
    <div class="row roomy padding-y-2xl align-stretch">
      <div
        class="column-12 laptop:column-4 margin-bottom-xl"
        style=""
      >
        <div
          class="card padding-top-0"
          style="--card-background: var(--example-color); height: 100%"
        >
          <div
            class="example-mobileapp"
            style="margin: 0 auto; background-color: var(--example-color-alt); border-radius: 0 0 1.5em 1.5em"
          >
            <h4 class="sr-only">An example of a mobile app using icons</h4>
            <div style="padding: var(--spacing-md); color: var(--example-color)">
              <i
                :class="[`fas fa-spinner-third fa-fw fa-2x fa-spin`]"
                style="--fa-animation-duration: 3s"
              />
              <span class="size-lg strong display-block margin-top-xs">Loading...</span>
            </div>
            <div
              class="display-flex flex-content-around"
              style="
                background-color: var(--fa-navy);
                padding: var(--spacing-sm) var(--spacing-xs);
                border-radius: 0 0 1.5em 1.5em;
                --button-border-radius: var(--border-radius-lg);
                --button-padding: var(--button-padding-vertical);
              "
            >
              <button
                class="flat"
                style="
                  --button-hover-background: var(--example-color-alt);
                  --button-color: var(--example-color-alt);
                  --button-background: var(--white);
                  --button-margin-bottom: 0;
                "
              >
                <font-awesome-icon
                  :icon="$v6.solid.faPaperPlane"
                  size="xl"
                  fixed-width
                />
                <span class="sr-only">example button</span>
              </button>
              <button
                class="flat"
                style="
                  --button-hover-background: var(--example-color-alt);
                  --button-color: var(--white);
                  --button-background: var(--example-color-alt);
                  --button-margin-bottom: 0;
                "
              >
                <i :class="[`${getIconStyle(icon.style)} fa-${icon.name} fa-fw fa-xl`]" />
                <span class="sr-only">example button</span>
              </button>
              <button
                class="flat"
                style="
                  --button-hover-background: var(--example-color-alt);
                  --button-color: var(--example-color-alt);
                  --button-background: var(--white);
                  --button-margin-bottom: 0;
                "
              >
                <font-awesome-icon
                  :icon="$v6.solid.faPhone"
                  size="xl"
                  fixed-width
                />
                <span class="sr-only">example button</span>
              </button>
              <button
                class="flat"
                style="
                  --button-hover-background: var(--example-color-alt);
                  --button-color: var(--example-color-alt);
                  --button-background: var(--white);
                  --button-margin-bottom: 0;
                "
              >
                <font-awesome-icon
                  :icon="$v6.solid.faComment"
                  size="xl"
                  fixed-width
                />
                <span class="sr-only">example button</span>
              </button>
              <button
                class="flat display-block laptop:display-none"
                style="
                  --button-hover-background: var(--example-color-alt);
                  --button-color: var(--example-color-alt);
                  --button-background: var(--white);
                  --button-margin-bottom: 0;
                "
              >
                <font-awesome-icon
                  :icon="$v6.solid.faBullhorn"
                  size="xl"
                  fixed-width
                />
                <span class="sr-only">example button</span>
              </button>
              <button
                class="flat display-block laptop:display-none"
                style="
                  --button-hover-background: var(--example-color-alt);
                  --button-color: var(--example-color-alt);
                  --button-background: var(--white);
                  --button-margin-bottom: 0;
                "
              >
                <font-awesome-icon
                  :icon="$v6.solid.faMicrophone"
                  size="xl"
                  fixed-width
                />
                <span class="sr-only">example button</span>
              </button>
              <button
                class="flat display-block laptop:display-none"
                style="
                  --button-hover-background: var(--example-color-alt);
                  --button-color: var(--example-color-alt);
                  --button-background: var(--white);
                  --button-margin-bottom: 0;
                "
              >
                <font-awesome-icon
                  :icon="$v6.solid.faCameraRetro"
                  size="xl"
                  fixed-width
                />
                <span class="sr-only">example button</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        class="column-12 tablet:column-6 laptop:column-4 margin-bottom-xl"
        style=""
      >
        <div
          class="card display-flex align-center"
          style="flex-direction: column; --card-background: var(--example-color-alt); height: 100%"
        >
          <div
            class="example-sign display-flex margin-bottom-lg"
            style="z-index: 2; border-radius: var(--border-radius-md); background-color: var(--white)"
          >
            <h4 class="sr-only">An example of a signs with icons</h4>
            <div
              class="text-left text-truncate padding-x-md padding-y-lg laptop:padding-x-lg"
              style="flex-grow: 3; color: var(--example-color-alt)"
            >
              <font-awesome-icon
                :icon="$v6.solid.faBus"
                size="2xl"
                fixed-width
              />
              <span
                class="margin-left-md"
                style="color: var(--fa-navy)"
              >
                Bus
              </span>
            </div>
            <div
              style="
                padding: var(--spacing-lg);
                background-color: var(--fa-navy);
                color: var(--white);
                border-radius: 0 var(--border-radius-md) var(--border-radius-md) 0;
              "
            >
              <font-awesome-icon
                :icon="$v6.solid.faArrowUp"
                size="xl"
                fixed-width
              />
            </div>
          </div>

          <div
            class="sign display-flex margin-bottom-lg"
            style="z-index: 2; border-radius: var(--border-radius-md); background-color: var(--white)"
          >
            <div
              class="text-left text-truncate padding-x-md padding-y-lg laptop:padding-x-lg"
              style="flex-grow: 3; color: var(--example-color-alt)"
            >
              <i :class="[`${getIconStyle('solid')} fa-fw fa-2xl fa-${icon.name}`]" />
              <span
                class="margin-left-md text-capitalize"
                style="color: var(--fa-navy)"
              >
                {{ icon.name }}
              </span>
            </div>
            <div
              style="
                padding: var(--spacing-lg);
                background-color: var(--fa-navy);
                color: var(--white);
                border-radius: 0 var(--border-radius-md) var(--border-radius-md) 0;
              "
            >
              <font-awesome-icon
                :icon="$v6.solid.faArrowRight"
                size="xl"
                fixed-width
              />
            </div>
          </div>
          <div style="z-index: 1; position: absolute; top: 0; left: 15%; width: 15px; height: 65%; background-color: var(--example-color)">&nbsp;</div>
          <div style="z-index: 1; position: absolute; top: 0; right: 15%; width: 15px; height: 65%; background-color: var(--example-color)">&nbsp;</div>
        </div>
      </div>

      <div
        class="column-12 tablet:column-6 laptop:column-4 margin-bottom-xl"
        style=""
      >
        <div
          class="card display-flex flex-column flex-content-end text-left padding-bottom-0"
          style="--card-background: var(--example-color); height: 100%"
        >
          <div
            class="example-todos text-capitalize text-center padding-xs"
            style="color: var(--example-color); background-color: var(--fa-navy); border-radius: var(--border-radius-md) var(--border-radius-md) 0 0"
          >
            <h4 class="sr-only">An example of a to-do list with icons</h4>
            Future to-do list
          </div>
          <div style="background-color: var(--white)">
            <ul class="fa-ul">
              <li style="padding: var(--spacing-sm); border-bottom: 1px solid var(--fa-gravy); color: var(--example-color-alt)">
                <span class="fa-li padding-y-4xs"><i class="far fa-check-square" /></span>
                <div class="text-left text-truncate padding-y-4xs">
                  <font-awesome-icon
                    :icon="todoListIcons.icon1"
                    size="xl"
                    fixed-width
                  />
                  <span class="text-truncate margin-left-md">{{ theme.icon1.label }}</span>
                </div>
              </li>
              <li style="padding: var(--spacing-sm); border-bottom: 1px solid var(--fa-gravy)">
                <span class="fa-li padding-y-4xs">
                  <i
                    class="far fa-square"
                    style="color: var(--example-color-alt)"
                  />
                </span>
                <div class="text-left text-truncate padding-y-4xs">
                  <i :class="[`${getIconStyle(lightestStyle)} fa-fw fa-xl fa-${icon.name}`]" />
                  <span
                    class="text-truncate text-capitalize margin-left-md"
                    style="color: var(--fa-navy)"
                  >
                    {{ icon.name }}
                  </span>
                </div>
              </li>
              <li style="padding: var(--spacing-sm)">
                <span class="fa-li padding-y-4xs">
                  <i
                    class="far fa-square"
                    style="color: var(--example-color-alt)"
                  />
                </span>
                <div class="text-left text-truncate padding-y-4xs">
                  <font-awesome-icon
                    :icon="todoListIcons.icon2"
                    size="xl"
                    fixed-width
                  />
                  <span
                    class="margin-left-md"
                    style="color: var(--fa-navy)"
                  >
                    {{ theme.icon2.label }}
                  </span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div
        class="column-12 laptop:column-6 margin-bottom-xl"
        style=""
      >
        <div
          class="card display-flex flex-column flex-content-end text-left padding-bottom-0"
          style="--card-background: var(--example-color); height: 100%"
        >
          <div
            class="examples-website display-flex padding-xs align-middle"
            style="color: var(--example-color); background-color: var(--fa-navy); border-radius: var(--border-radius-md) var(--border-radius-md) 0 0"
          >
            <h4 class="sr-only">An example of a website with icons</h4>
            <div class="display-flex align-middle">
              <div
                class="display-inline-block margin-left-3xs"
                style="width: 15px; height: 15px; background-color: var(--fa-dk-navy); border-radius: var(--border-radius-lg)"
              >
                &nbsp;
              </div>
              <div
                class="display-inline-block margin-left-3xs"
                style="width: 15px; height: 15px; background-color: var(--fa-dk-navy); border-radius: var(--border-radius-lg)"
              >
                &nbsp;
              </div>
              <div
                class="display-inline-block margin-left-3xs"
                style="width: 15px; height: 15px; background-color: var(--fa-dk-navy); border-radius: var(--border-radius-lg)"
              >
                &nbsp;
              </div>
            </div>
            <div
              class="text-center padding-x-4xl padding-y-4xs margin-x-2xl"
              style="flex-grow: 2; opacity: 0.7; background-color: var(--fa-dk-navy); border-radius: var(--border-radius-lg)"
            >
              {{ icon.name }}-opedia
            </div>
          </div>
          <div
            class="display-flex"
            style="background-color: var(--example-color-alt); color: var(--example-color)"
          >
            <div
              class="padding-x-sm padding-y-md text-center"
              style="border-right: 2px solid var(--example-color); flex-grow: 1"
            >
              <font-awesome-icon
                :icon="$v6.solid.faHouseChimney"
                size="lg"
                class="margin-right-4xs"
              />
              <span>Home</span>
            </div>
            <div
              class="padding-x-sm padding-y-md text-center"
              style="border-right: 2px solid var(--example-color); color: var(--white); flex-grow: 1"
            >
              <i :class="[`${getIconStyle('solid')} fa-lg fa-${icon.name} margin-right-4xs`]" />
              <span>About</span>
            </div>
            <div
              class="padding-x-sm padding-y-md text-center"
              style="border-right: 2px solid var(--example-color); flex-grow: 1"
            >
              <font-awesome-icon
                :icon="$v6.solid.faDollarCircle"
                size="lg"
                class="margin-right-4xs"
              />
              <span>Pricing</span>
            </div>
            <div
              class="padding-x-sm padding-y-md text-center"
              style="flex-grow: 1"
            >
              <font-awesome-icon
                :icon="$v6.solid.faUserCircle"
                size="lg"
                class="margin-right-4xs"
              />
              <span>Account</span>
            </div>
          </div>

          <div
            class="display-flex flex-items-center padding-x-2xl padding-y-lg"
            style="background-color: var(--white); color: var(--fa-gravy)"
          >
            <span
              class="fa-stack fa-2x"
              style="flex-shrink: 0"
            >
              <i class="far fa-circle fa-stack-2x" />
              <i :class="[`${getIconStyle('solid')} fa-${icon.name} fa-stack-1x`]" />
            </span>
            <span
              class="margin-left-md size-lg text-truncate"
              style="line-height: 1.2"
            >
              We're your place for all things
              <span class="laptop:display-block">{{ icon.name }}.</span>
            </span>
          </div>
        </div>
      </div>

      <div
        class="column-12 tablet:column-6 laptop:column-6 margin-bottom-xl"
        style=""
      >
        <div
          class="card padding-x-3xl laptop:padding-x-6xl"
          style="--card-background: var(--fa-navy); --card-color: var(--white); height: 100%"
        >
          <div
            class="examples-transitline display-flex flex-content-center flex-column"
            style="height: 100%"
          >
            <h4 class="sr-only">An example of a transit line using icons</h4>
            <div class="display-flex flex-content-between">
              <font-awesome-icon
                :icon="$v6.solid.faTrees"
                size="2x"
                fixed-width
                style="color: var(--example-color)"
              />
              <i
                :class="[`${getIconStyle('solid')} fa-${icon.name} fa-fw fa-2x fa-beat-fade`]"
                style="color: var(--example-color); --fa-animation-duration: 2s"
              />
              <font-awesome-icon
                :icon="$v6.light.faHouse"
                size="2x"
                fixed-width
                style="color: var(--fa-gravy)"
              />
              <font-awesome-icon
                :icon="$v6.light.faBuildings"
                size="2x"
                fixed-width
                style="color: var(--fa-gravy)"
              />
            </div>
            <div
              class="position-relative display-flex margin-y-3xl margin-x-sm size-xs"
              style="height: 1em"
            >
              <div
                class="position-absolute z-index-2"
                style="
                  top: -0.5em;
                  left: 0;
                  border: 0.5em solid var(--white);
                  height: 2em;
                  width: 2em;
                  border-radius: var(--border-radius-lg);
                  background-color: var(--example-color);
                "
              >
                &nbsp;
              </div>

              <div
                class="padding-4xs"
                style="flex-grow: 2; background: var(--white); border-radius: var(--border-radius-lg)"
              >
                <div style="border-top: 0.5em solid var(--example-color); height: 1px">&nbsp;</div>
              </div>

              <div
                class="position-absolute z-index-2"
                style="
                  top: -0.5em;
                  left: 31%;
                  border: 0.5em solid var(--white);
                  height: 2em;
                  width: 2em;
                  border-radius: var(--border-radius-lg);
                  background-color: var(--example-color);
                "
              >
                &nbsp;
              </div>

              <div
                class="padding-4xs"
                style="flex-grow: 2; background: var(--white); border-radius: var(--border-radius-lg)"
              >
                <div style="border-top: 0.5em dotted var(--example-color); height: 1px">&nbsp;</div>
              </div>

              <div
                class="position-absolute z-index-2"
                style="
                  top: -0.5em;
                  right: 31%;
                  border: 0.5em solid var(--white);
                  height: 2em;
                  width: 2em;
                  border-radius: var(--border-radius-lg);
                  background-color: var(--example-color);
                "
              >
                &nbsp;
              </div>

              <div
                class="padding-4xs"
                style="flex-grow: 2; background: var(--white); border-radius: var(--border-radius-lg)"
              >
                <div style="border-top: 0.5em solid var(--white); height: 1px">&nbsp;</div>
              </div>

              <div
                class="position-absolute z-index-2"
                style="
                  top: -0.5em;
                  right: 0;
                  border: 0.5em solid var(--white);
                  height: 2em;
                  width: 2em;
                  border-radius: var(--border-radius-lg);
                  background-color: var(--example-color);
                "
              >
                &nbsp;
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="column-12 tablet:column-6 laptop:column-4 margin-bottom-xl"
        style=""
      >
        <div
          class="card display-flex flex-content-center flex-items-center"
          style="
            color: var(--white);
            height: 100%;
            background: center / cover var(--fa-navy) url('https://img.fortawesome.com/1ce05b4b/background-bricks2.png') repeat;
          "
        >
          <i :class="[`${getIconStyle('solid')} fa-${icon.name} fa-fw fa-6x`]" />
        </div>
      </div>

      <div
        class="column-12 tablet:column-6 laptop:column-4 margin-bottom-xl"
        style=""
      >
        <div
          class="card"
          style="--card-background: var(--example-color); height: 100%"
        >
          <div
            class="examples-buttons display-flex flex-column flex-content-center"
            style="height: 100%"
          >
            <h4 class="sr-only">An example of a buttons with icons</h4>
            <button
              class="block"
              style="--button-active-background: var(--example-color-alt); --button-hover-background: var(--example-color)"
            >
              <i
                :class="[`${getIconStyle('light')} fa-${icon.name} fa-fw fa-xl margin-right-md`]"
                style="color: var(--example-color-alt)"
              />
              {{ theme.button.default }}
            </button>
            <button
              class="block active"
              style="--button-active-background: var(--example-color-alt); --button-hover-background: var(--example-color)"
            >
              <i
                :class="[`${getIconStyle('light')} fa-${icon.name} fa-fw fa-xl margin-right-md fa-spin`]"
                style="color: var(--white); --fa-animation-duration: 2s"
              />
              {{ theme.button.active }}
            </button>
          </div>
        </div>
      </div>

      <div
        class="column-12 tablet:column-6 laptop:column-4 margin-bottom-xl"
        style=""
      >
        <div
          class="card"
          style="--card-background: var(--fa-navy); height: 100%"
        >
          <div
            class="examples-notification tablet:size-sm laptop:size-md display-flex flex-items-center flex-content-center"
            style="height: 100%"
          >
            <h4 class="sr-only">An example of a notification with icons</h4>
            <div
              class="card display-flex flex-row flex-items-center padding-0 size-md tablet:size-sm laptop:size-xs"
              style="--card-background: var(--white); --card-margin-bottom: 0; align-items: stretch"
            >
              <div
                class="display-flex flex-items-center padding-xl"
                style="flex-grow: 0"
              >
                <i
                  :class="[`${getIconStyle(lightestStyle)} fa-${icon.name} fa-fw fa-3x`]"
                  style="color: var(--example-color)"
                />
              </div>
              <div
                class="display-flex flex-items-center padding-xl text-left size-lg"
                style="background-color: var(--example-color); border-radius: 0 var(--border-radius-sm) var(--border-radius-sm) 0"
              >
                <span>{{ theme.alert }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="column-12 laptop:column-6 margin-bottom-xl"
        style=""
      >
        <div
          class="card padding-0"
          style="--card-background: var(--example-color); height: 100%"
        >
          <div
            class="examples-sizing display-flex flex-column"
            style="height: 100%"
          >
            <h4 class="sr-only">An example of a icons sized and stacked</h4>
            <div
              class="display-flex flex-items-center flex-content-between padding-y-lg padding-right-5xl padding-left-4xl laptop:padding-right-4xl laptop:padding-left-4xl"
              style="
                color: var(--example-color);
                background-color: var(--fa-navy);
                border-radius: var(--border-radius-md) var(--border-radius-md) 0 0;
                flex-grow: 1;
              "
            >
              <span
                class="fa-stack fa-4x"
                style="flex-shrink: 0"
              >
                <i class="fas fa-circle fa-stack-2x" />
                <i
                  :class="[`${getIconStyle(lightestStyle)} fa-${icon.name} fa-stack-1x fa-inverse`]"
                  style="--fa-inverse: var(--fa-navy)"
                />
              </span>
              <span
                class="fa-stack fa-3x"
                style="flex-shrink: 0"
              >
                <i class="fas fa-circle fa-stack-2x" />
                <i
                  :class="[`${getIconStyle('light')} fa-${icon.name} fa-stack-1x fa-inverse`]"
                  style="--fa-inverse: var(--fa-navy)"
                />
              </span>
              <span
                class="fa-stack fa-2x"
                style="flex-shrink: 0"
              >
                <i class="fas fa-circle fa-stack-2x" />
                <i
                  :class="[`${getIconStyle('regular')} fa-${icon.name} fa-stack-1x fa-inverse`]"
                  style="--fa-inverse: var(--fa-navy)"
                />
              </span>
              <span
                class="fa-stack fa-1x"
                style="flex-shrink: 0"
              >
                <i class="fas fa-circle fa-stack-2x" />
                <i
                  :class="[`${getIconStyle('solid')} fa-${icon.name} fa-stack-1x fa-inverse`]"
                  style="--fa-inverse: var(--fa-navy)"
                />
              </span>
            </div>
            <div
              class="display-flex flex-items-center flex-content-between padding-y-lg padding-left-5xl padding-right-4xl laptop:padding-left-4xl laptop:padding-right-4xl"
              style="flex-grow: 1"
            >
              <span
                class="fa-stack fa-1x"
                style="flex-shrink: 0"
              >
                <i class="fas fa-square fa-stack-2x" />
                <i :class="[`${getIconStyle('solid')} fa-${icon.name} fa-stack-1x fa-inverse`]" />
              </span>
              <span
                class="fa-stack fa-2x"
                style="flex-shrink: 0"
              >
                <i class="fas fa-square fa-stack-2x" />
                <i :class="[`${getIconStyle('regular')} fa-${icon.name} fa-stack-1x fa-inverse`]" />
              </span>
              <span
                class="fa-stack fa-3x"
                style="flex-shrink: 0"
              >
                <i class="fas fa-square fa-stack-2x" />
                <i :class="[`${getIconStyle('light')} fa-${icon.name} fa-stack-1x fa-inverse`]" />
              </span>
              <span
                class="fa-stack fa-4x"
                style="flex-shrink: 0"
              >
                <i class="fas fa-square fa-stack-2x" />
                <i :class="[`${getIconStyle(lightestStyle)} fa-${icon.name} fa-stack-1x fa-inverse`]" />
              </span>
            </div>
          </div>
        </div>
      </div>

      <div
        class="column-12 laptop:column-6 margin-bottom-xl"
        style=""
      >
        <div
          class="card padding-x-3xl laptop:padding-x-6xl"
          style="--card-color: var(--fa-navy); --card-background: var(--example-color); height: 100%"
        >
          <div
            class="examples-sizing display-flex flex-content-center flex-column"
            style="height: 100%"
          >
            <h4 class="sr-only">An example of pull quote with an icon</h4>
            <div class="size-lg laptop:size-xl text-left">
              <span
                class="fa-stack fa-2x fa-pull-left"
                style="width: 2em"
              >
                <i class="fas fa-square fa-stack-2x" />
                <i :class="[`${getIconStyle('solid')} fa-${icon.name} fa-stack-1x fa-inverse`]" />
              </span>

              <p>&ldquo;{{ theme.quote }}&rdquo;</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import sample from 'lodash/sample'
  import { getShortCode } from 'app/releases.js'
  import { createWidgetMixin } from 'vue-instantsearch'
  import { connectHits } from 'instantsearch.js/es/connectors'

  const detailColors = require('./detailColors.json')
  const detailThemes = require('./detailThemes.json')

  export default {
    name: 'IconExamples',

    mixins: [createWidgetMixin({ connector: connectHits })],

    props: {
      icon: {
        type: Object
      },
      selectedFamily: {
        type: String
      }
    },

    data() {
      return {
        previewMode: 'day',
        color: sample(detailColors),
        theme: sample(detailThemes)
      }
    },

    computed: {
      lightestStyle() {
        return this.$releases.active.versionParts.major === 6 ? 'thin' : 'light'
      },
      todoListIcons() {
        const style = this.lightestStyle

        return {
          icon1: this.$v6[style][this.theme.icon1.icon],
          icon2: this.$v6[style][this.theme.icon2.icon]
        }
      }
    },

    methods: {
      setPreviewMode(mode) {
        this.previewMode = mode
      },

      getIconStyle(style) {
        try {
          return getShortCode(this.selectedFamily, style)
        } catch (e) {
          return 'fas' // family style combo does not exist so default to solid
        }
      }
    }
  }
</script>
